/**
 * The ONE place the live-game lane writes to `GamePlayStore`.
 *
 * Other screens (ChallengeComponent, Header, Dashboard, Classroom) read those
 * cross-screen flags, so V2 must mirror exactly what PlayChess writes inside
 * the same transitions. Routing every write through this module is what makes
 * `__tests__/parity/storeWriteParity.test.ts` able to enumerate the bridge.
 *
 * The reducers stay otherwise pure: they return one new state object and call
 * only these named bridge functions (never zustand directly).
 *
 * NO LANE GATE on this platform: the web RELOADS the tab when a lane that has
 * already registered its socket handlers has to hand over to the other one
 * (`PlayChessSwitch`), so two lanes can never be writing these flags at once.
 * The app copy of this file gates every function on
 * `Screens/PlayChessV2/hooks/v2Active` instead — see `SYNC.md`.
 */
import useArenaStore from '@/Store/ArenaStore';
import useGamePlayState from '@/Store/GamePlayStore';

import {ArenaStandings, ArenaStatus} from './state';

/**
 * Every GamePlayStore setter the old screen calls, and where V2 drives it.
 * Keep this list in sync with PlayChess — the parity test compares it against
 * every `gameplayStore.set*(` in src/Screens/PlayChess/index.js.
 *
 * milestone:
 *   'M0' — bridged now
 *   'M2'/'M4'/'M5' — challenge / tournament / arena families, still TODO
 */
export const BRIDGED_SETTERS: {
  [setter: string]: {milestone: string; note: string};
} = {
  setPlaying: {
    milestone: 'M0',
    note: 'true on start_game_response/start_game, false on game_end_response / no_ongoing_game / eval-pending (PlayChess L2301, L2714, L3035, L2857)',
  },
  setShowEndGameModal: {
    milestone: 'M0',
    note: 'false on start_game_response (L2305); true from useGameplayEffects after result.modalDelayMs, not from the reducer (PlayChess L3050-3057)',
  },
  setTournamentPlay: {
    milestone: 'M0',
    note: 'false after fetch_game_state (L3922) and on unmount (L397-399); true on tournament entry in M4',
  },
  setShowChallengeReject: {
    milestone: 'M2',
    note: 'true on challenge_reject (L2795) -> bridgeSetShowChallengeReject, from reducers/challenge.ts; the global ChallengeComponent closes its popup on it',
  },
  setShowChallengeCancel: {
    milestone: 'M2',
    note: 'true on challenge_cancel (L2441) -> bridgeSetShowChallengeCancel, from reducers/challenge.ts; the global ChallengeComponent DRAWS "Challenge Cancelled!" from it',
  },
  setShowChallengeExpire: {
    milestone: 'M2',
    note: 'challenge_request_expire is DISMISS-ONLY: the old screen only ever sets false (L2812, L2822) -> bridgeSetShowChallengeExpire(false), from reducers/challenge.ts',
  },
  setShowChangeInClient: {
    milestone: 'M2',
    note: 'cleared on first render (L352-356) and by "Play here" (L4237) -> bridgeSetShowChangeInClient, from Screens/PlayChessV2/overlay/ClientChanged/model.ts (fix I, 2026-10-06; previously Strip/model.ts, before the card took this face over)',
  },
  setJoinedTournament: {
    milestone: 'M4',
    note: 'true on a successful join of mine (L1840), false on my withdraw (L1899) and on tournament_end (L1995) -> bridgeSetJoinedTournament, from reducers/tournament.ts; reset on mount (L532) from Screens/PlayChessV2/index.tsx',
  },
  setJoinedTournamentId: {
    milestone: 'M4',
    note: 'the joined tournament id, same four transitions as setJoinedTournament (L1841, L1900, L1996, L533) -> bridgeSetJoinedTournamentId',
  },
  setJoinFailure: {
    milestone: 'M4',
    note: 'consumed (set to null) once the failure has been surfaced (L1027) -> bridgeSetJoinFailure(null), from actions.clearTournamentJoinState',
  },
};

/**
 * ArenaStore surface PlayChess drives — WIRED by the app lane's A3a milestone,
 * mirrored here the same day (`SYNC.md`).
 *
 * The V2 lane reduces arena messages into `state.arena` (`reducers/arena.ts`)
 * and mirrors the result into the SAME `useArenaStore` the old screen writes, so
 * the arena lobby / berserk control keep reading what they always read.
 *
 * The mirror is per MESSAGE, not a diff of the slice: `setStatus` merges a
 * PARTIAL payload and `setBerserk` decides which side's flag to raise from
 * `(userId, payload, myUserId)`, so a diff would have to re-invent both rules.
 */
export const BRIDGED_ARENA_SETTERS: {
  [setter: string]: {milestone: string; note: string};
} = {
  setStatus: {
    milestone: 'M5',
    note: 'arena_status_response + the optimistic pooled/paused writes -> bridgeSetArenaStatus',
  },
  setStandings: {
    milestone: 'M5',
    note: 'arena_standings_response -> bridgeSetArenaStandings',
  },
  setBerserk: {
    milestone: 'M5',
    note: 'arena_berserk_response -> bridgeSetArenaBerserk',
  },
  setIsJoined: {
    milestone: 'M5',
    note: 'arena join state -> bridgeSetArenaIsJoined',
  },
  resetGameBerserk: {
    milestone: 'M5',
    note: 'start/end of an arena game -> bridgeResetGameBerserk',
  },
  resetArena: {
    milestone: 'M5',
    note: 'unmount / tournament back -> bridgeResetArena, from actions.setTournamentContext',
  },
};

/** PlayChess L2301/L2714: a live board means `playing`. */
export function bridgeSetPlaying(value: boolean): void {
  useGamePlayState.getState().setPlaying(value);
}

/** PlayChess L2305 (clear) and L3057 (set, from the effects hook). */
export function bridgeSetShowEndGameModal(value: boolean): void {
  useGamePlayState.getState().setShowEndGameModal(value);
}

/** PlayChess L3922 (after fetch_game_state) and L397-399 (unmount). */
export function bridgeSetTournamentPlay(value: boolean): void {
  useGamePlayState.getState().setTournamentPlay(value);
}

// ── the CROSS-SCREEN notice flags ─────────────────────────

/**
 * PlayChess L2795 (`challengeRejectedCallback`) and L3783 (dismiss).
 *
 * The app-wide `Components/ChallengeComponent` reads it to CLOSE an incoming
 * challenge popup (index L145-157) — the visible "Challenge Rejected!" modal is
 * the Play screen's own, which on this lane is `classic/ChallengeNotice`.
 */
export function bridgeSetShowChallengeReject(value: boolean): void {
  useGamePlayState.getState().setShowChallengeReject(value);
}

/**
 * PlayChess L2441 (`challengeCancelCallback`) and L3781 (dismiss).
 *
 * THIS is the one that has to survive an unmounted Play screen (P1-10): the
 * app-wide `ChallengeComponent` DRAWS "Challenge Cancelled!" from it (index
 * L574-577), which is how a user anywhere in the app learns that the challenge
 * they sent was withdrawn.
 */
export function bridgeSetShowChallengeCancel(value: boolean): void {
  useGamePlayState.getState().setShowChallengeCancel(value);
}

/**
 * PlayChess L2812 / L2822 — DISMISS-ONLY, exactly as the old screen has it:
 * `challenge_request_expire` is reused by the server as "accepted elsewhere"
 * and arena auto-pairing fires it between games, so the old screen only ever
 * sets FALSE here and never raises an "expired" modal of its own.
 */
export function bridgeSetShowChallengeExpire(value: boolean): void {
  useGamePlayState.getState().setShowChallengeExpire(value);
}

/**
 * PlayChess L4237 (`handleContinue`): "Play here" clears the
 * you-are-playing-elsewhere sheet and then reconnects. The V2 Strip's own
 * `onPlayHere` only reconnected, so the sheet never went away. Fix I
 * (2026-10-06) moved this call from the Strip's (deleted) `onPlayHere` to
 * `overlay/ClientChanged/model.ts`'s `onContinue` — the full-screen card,
 * not the Strip, owns this face now.
 */
export function bridgeSetShowChangeInClient(value: boolean): void {
  useGamePlayState.getState().setShowChangeInClient(value);
}

/**
 * PlayChess L1027 (`gameplayStore.setJoinFailure(null)` once the failure has
 * been surfaced). `SocketProvider` writes the failure for whichever screen is
 * up; the screen that surfaced it CONSUMES it, or a stale one outlives its
 * tournament in the persisted store.
 */
export function bridgeSetJoinFailure(value: any): void {
  useGamePlayState.getState().setJoinFailure(value);
}

/**
 * PlayChess L1840 / L1899 / L1995 — "this user is in a tournament", read by
 * `Screens/FunChallenges/BattleLobby` as a busy gate (L336-337) and never
 * written on this lane before fix E9: a V2 player in a tournament looked free
 * to the Fun Battle lobby.
 */
export function bridgeSetJoinedTournament(value: boolean): void {
  useGamePlayState.getState().setJoinedTournament(value);
}

/**
 * PlayChess L1841 / L1900 / L1996, and `'0'` is its "none" sentinel (L533,
 * `GamePlayStore` L72) — the shared reducer passes `null` for it, because the
 * sentinel is this store's convention and not the core's.
 *
 * `Screens/Classroom` refetches the active mini-tournament whenever this
 * changes (L993, L1753-1757), so the WITHDRAW/END writes matter as much as the
 * join: `SocketProvider` sets the id on a join response of its own accord
 * (L275-281), but nothing outside the play screen ever clears it.
 */
export function bridgeSetJoinedTournamentId(value: string | null): void {
  useGamePlayState
    .getState()
    .setJoinedTournamentId(value === null ? '0' : value);
}

// ── ArenaStore ────────────────────────────────────────────

/** arena_status_response + the optimistic writes — the store MERGES a patch. */
export function bridgeSetArenaStatus(patch: Partial<ArenaStatus>): void {
  useArenaStore.getState().setStatus(patch);
}

/** arena_standings_response. */
export function bridgeSetArenaStandings(standings: ArenaStandings): void {
  useArenaStore.getState().setStandings(standings);
}

/**
 * arena_berserk_response. The store owns the "which side" rule (and the refusal
 * early-return), so the three arguments are passed through verbatim.
 */
export function bridgeSetArenaBerserk(
  userId: string | number,
  payload: {refused?: string; newClockMs?: number; incrementMs?: number},
  myUserId?: string | number,
): void {
  useArenaStore.getState().setBerserk(userId, payload, myUserId);
}

/** the withdraw path; the join side is the screen's. */
export function bridgeSetArenaIsJoined(value: boolean): void {
  useArenaStore.getState().setIsJoined(value);
}

/** the start and the end of an arena game. */
export function bridgeResetGameBerserk(): void {
  useArenaStore.getState().resetGameBerserk();
}

/** unmount / backing out of a tournament. */
export function bridgeResetArena(): void {
  useArenaStore.getState().resetArena();
}
